import { BEAT_SOUNDS, isBeatSound } from '../core/beat-sounds.js';

// Count-ins and the metronome share the instrument's browser audio session.
export class BeatAudio {
  constructor(music, fetcher = (...args) => fetch(...args)) {
    this.music = music; this.fetcher = fetcher; this.buffers = new Map(); this.voices = new Set();
  }
  async load(context, sound) {
    if (sound === 'beep' || sound === 'silent') return;
    if (!this.buffers.has(sound)) {
      const pending = (async () => {
        const response = await this.fetcher(BEAT_SOUNDS.find(option => option.id === sound).file);
        if (!response.ok) throw new Error('Could not load the rhythm sound. Please try again.');
        return context.decodeAudioData(await response.arrayBuffer());
      })();
      this.buffers.set(sound, pending);
      pending.catch(() => { if (this.buffers.get(sound) === pending) this.buffers.delete(sound); });
    }
    const buffer = await this.buffers.get(sound);
    this.buffers.set(sound, buffer);
  }
  async prepare(sound) {
    if (!isBeatSound(sound)) return null;
    const context = this.music.ensureAudio();
    if (context !== this.observedContext) {
      this.observedContext?.removeEventListener?.('statechange', this.stateChange);
      this.observedContext = context;
      this.stateChange = () => { if (context.state !== 'running') this.stop(); };
      context.addEventListener?.('statechange', this.stateChange);
    }
    await Promise.all([this.music.resumeAudio(context), this.load(context, sound)]);
    if (context !== this.music.context || context.state !== 'running') return null;
    return context;
  }
  play(context, sound, at = context.currentTime, volume = 70, channel = 'countdown') {
    if (context.state !== 'running' || !isBeatSound(sound) || volume <= 0) return;
    const buffer = this.buffers.get(sound);
    if (sound !== 'beep' && !(buffer?.duration > 0)) return;
    const source = sound === 'beep' ? context.createOscillator() : context.createBufferSource();
    const gain = context.createGain(), start = Math.max(context.currentTime, at);
    if (sound === 'beep') {
      source.frequency.value = 880;
      gain.gain.setValueAtTime(.22 * volume / 100, start);
      gain.gain.exponentialRampToValueAtTime(.001, start + .06);
    } else { source.buffer = buffer; gain.gain.value = .7 * volume / 100; }
    source.connect(gain); gain.connect(context.destination);
    const voice = { channel, stop: () => source.stop() };
    this.voices.add(voice);
    source.onended = () => { this.voices.delete(voice); source.disconnect(); gain.disconnect(); };
    source.start(start);
    if (sound === 'beep') source.stop(start + .065);
    return voice;
  }
  stop(channel) {
    for (const voice of this.voices) if (!channel || voice.channel === channel) { voice.stop(); this.voices.delete(voice); }
  }
}
